'use client';

import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { type ContactVerificationChannel, MFA_SMS_OTP_CONTRACT, TIME } from '@repo/common';

import { trackFunnelStep } from '../../contexts/analytics-context';
import type { FunnelStepPayloads } from '../../utils/funnel-steps';
import type {
  SmsMarketingCodeRequestResult,
  SmsMarketingFailureReason,
  SmsMarketingPendingVerification,
  SmsMarketingSignupRequest,
  SmsMarketingVerifyResult,
} from './sms-marketing-signup-types';

export const SMS_CODE_DIGITS = MFA_SMS_OTP_CONTRACT.digits;
const CODE_PATTERN = new RegExp(`^\\d{${SMS_CODE_DIGITS}}$`);
const NETWORK_FAILURE = { status: 'failed', reason: 'network' } as const;
/** The server has dropped the code, so the person starts again from the number. */
const CODE_ENDING_FAILURES: ReadonlySet<SmsMarketingFailureReason> = new Set([
  'code-expired',
  'too-many-attempts',
  'number-unavailable',
  'sign-in-required',
]);
/** The number cannot be used here, so it is cleared rather than offered again. */
const REFUSED_NUMBER_FAILURES: ReadonlySet<SmsMarketingFailureReason> = new Set([
  'number-unavailable',
  'sign-in-required',
]);

export type SmsMarketingBusyAction = 'submit' | 'verify' | 'resend';

export type SmsMarketingSignupStep =
  | { kind: 'details' }
  | { kind: 'code'; maskedPhone: string; resendAvailableAt: Date }
  | { kind: 'verified'; subscribed: boolean }
  | { kind: 'already-enrolled' };

export interface SmsMarketingSignupDetails {
  email: string;
  phone: string;
  consented: boolean;
}

/** Where a call left the flow: waiting for a code, finished, or refused with the reason on show. */
export type SmsMarketingFlowOutcome = 'code-sent' | 'finished' | 'failed';

export interface SmsMarketingSignupFlowOptions {
  /** Where the flow runs. Surfaces that name themselves report their code sends and verifications as funnel steps. */
  funnelSurface?: FunnelStepPayloads['sms_code_verified']['surface'];
  /**
   * Wraps the code field, which takes focus whenever a code is waiting. The surface creates its refs, so the
   * flow it reads during render holds none.
   */
  codeRef: RefObject<HTMLDivElement | null>;
  /** Wraps the result message, which takes focus once the number is proven; surfaces without one leave it out. */
  resultRef?: RefObject<HTMLElement | null>;
  /** Resumes the code step, including when it arrives after the first render, unless the person already started. */
  pendingVerification?: SmsMarketingPendingVerification;
  onSubmit: (request: SmsMarketingSignupRequest) => Promise<SmsMarketingCodeRequestResult>;
  onVerify: (code: string) => Promise<SmsMarketingVerifyResult>;
  onResend: (channel?: ContactVerificationChannel) => Promise<SmsMarketingCodeRequestResult>;
  /** Called once the number is proven, whether the signup is new or already existed. */
  onVerified?: () => void;
  /** The person dropped the pending code to enter a different number. */
  onStartOver?: () => void;
}

export interface SmsMarketingSignupFlow {
  step: SmsMarketingSignupStep;
  details: SmsMarketingSignupDetails;
  code: string;
  failure: SmsMarketingFailureReason | undefined;
  busyAction: SmsMarketingBusyAction | undefined;
  isCodeComplete: boolean;
  updateDetails: (update: Partial<SmsMarketingSignupDetails>) => void;
  updateCode: (value: string) => void;
  submit: (request: SmsMarketingSignupRequest) => Promise<SmsMarketingFlowOutcome>;
  verify: () => Promise<SmsMarketingFlowOutcome>;
  resend: (channel?: ContactVerificationChannel) => Promise<SmsMarketingFlowOutcome>;
  changeNumber: () => void;
}

function codeStep(pending: SmsMarketingPendingVerification): SmsMarketingSignupStep {
  return {
    kind: 'code',
    maskedPhone: pending.maskedPhone,
    resendAvailableAt: new Date(Date.now() + pending.resendAfterSeconds * TIME.MILLISECONDS.PER_SECOND),
  };
}

/** A thrown request is shown as a retryable failure instead of breaking the form, and logged for diagnosis. */
async function withNetworkFallback<T>(request: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await request();
  } catch (error) {
    console.error('[SmsMarketingSignup] request failed', error);
    return fallback;
  }
}

/**
 * The number, consent and code steps behind every SMS signup surface. Surfaces
 * draw their own fields and buttons; the failure, focus and resume rules live here.
 */
export function useSmsMarketingSignupFlow({
  funnelSurface,
  codeRef,
  resultRef,
  pendingVerification,
  onSubmit,
  onVerify,
  onResend,
  onVerified,
  onStartOver,
}: SmsMarketingSignupFlowOptions): SmsMarketingSignupFlow {
  const [step, setStep] = useState<SmsMarketingSignupStep>(() =>
    pendingVerification ? codeStep(pendingVerification) : { kind: 'details' },
  );
  const [details, setDetails] = useState<SmsMarketingSignupDetails>({ email: '', phone: '', consented: false });
  const [code, setCode] = useState('');
  const [failure, setFailure] = useState<SmsMarketingFailureReason>();
  const [busyAction, setBusyAction] = useState<SmsMarketingBusyAction>();
  const hasStarted = useRef(false);
  const previousStepKind = useRef(step.kind);

  useEffect(() => {
    if (pendingVerification && !hasStarted.current) {
      // Restoring a code is not a step the person took, so it does not move focus or scroll the page.
      previousStepKind.current = 'code';
      setStep(codeStep(pendingVerification));
    }
  }, [pendingVerification]);

  // Focus follows step changes only, so a resumed page does not jump to the form on load.
  useEffect(() => {
    if (previousStepKind.current === step.kind) {
      return;
    }
    previousStepKind.current = step.kind;
    if (step.kind === 'code') {
      codeRef.current?.querySelector('input')?.focus();
    } else if (step.kind === 'verified' || step.kind === 'already-enrolled') {
      resultRef?.current?.focus();
    }
  }, [step.kind, codeRef, resultRef]);

  // After a failed code the cleared field takes focus again, so the next code can be typed straight away.
  useEffect(() => {
    if (failure && step.kind === 'code') {
      codeRef.current?.querySelector('input')?.focus();
    }
  }, [failure, step.kind, codeRef]);

  async function run<T>(
    action: SmsMarketingBusyAction,
    request: () => Promise<T>,
    fallback: T,
    apply: (result: T) => SmsMarketingFlowOutcome,
  ): Promise<SmsMarketingFlowOutcome> {
    hasStarted.current = true;
    setBusyAction(action);
    setFailure(undefined);
    const result = await withNetworkFallback(request, fallback);
    setBusyAction(undefined);
    return apply(result);
  }

  function restart(clearPhone = false): void {
    hasStarted.current = true;
    // Every new code needs fresh consent, as the number may change.
    setDetails(current => ({ ...current, phone: clearPhone ? '' : current.phone, consented: false }));
    setStep({ kind: 'details' });
  }

  function fail(reason: SmsMarketingFailureReason): SmsMarketingFlowOutcome {
    if (step.kind === 'code' && CODE_ENDING_FAILURES.has(reason)) {
      restart(REFUSED_NUMBER_FAILURES.has(reason));
    }
    setFailure(reason);
    return 'failed';
  }

  function applyCodeRequest(result: SmsMarketingCodeRequestResult, isResend: boolean): SmsMarketingFlowOutcome {
    if (result.status === 'code-sent') {
      if (funnelSurface) void trackFunnelStep('sms_code_sent', { surface: funnelSurface, resend: isResend });
      setCode('');
      setStep(codeStep(result));
      return 'code-sent';
    }
    if (result.status === 'confirmed') {
      setStep({ kind: 'verified', subscribed: result.subscribed === true });
      onVerified?.();
      return 'finished';
    }
    return fail(result.reason);
  }

  function applyVerification(result: SmsMarketingVerifyResult): SmsMarketingFlowOutcome {
    if (result.status === 'failed') {
      setCode('');
      return fail(result.reason);
    }
    setStep(
      result.status === 'verified'
        ? { kind: 'verified', subscribed: result.subscribed === true }
        : { kind: 'already-enrolled' },
    );
    if (funnelSurface && result.status === 'verified')
      void trackFunnelStep('sms_code_verified', { surface: funnelSurface });
    onVerified?.();
    return 'finished';
  }

  return {
    step,
    details,
    code,
    failure,
    busyAction,
    isCodeComplete: CODE_PATTERN.test(code),
    updateDetails: update => {
      hasStarted.current = true;
      setDetails(current => ({ ...current, ...update }));
    },
    updateCode: value => {
      hasStarted.current = true;
      setCode(value.replace(/\D/g, '').slice(0, SMS_CODE_DIGITS));
    },
    submit: request =>
      run(
        'submit',
        () => onSubmit(request),
        NETWORK_FAILURE,
        result => applyCodeRequest(result, false),
      ),
    verify: () => run('verify', () => onVerify(code), NETWORK_FAILURE, applyVerification),
    resend: channel =>
      run(
        'resend',
        () => onResend(channel),
        NETWORK_FAILURE,
        result => applyCodeRequest(result, true),
      ),
    changeNumber: () => {
      restart();
      setFailure(undefined);
      onStartOver?.();
    },
  };
}
